// src/pages/ChatsPage.jsx (or your path)
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../services/firebase';
import '../styles/ChatsPage.css';

function ChatsPage() {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Keep track of unsubscribers so we can clean up
    let unsubscribers = [];

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/auth/login');
        return;
      }

      try {
        // Ensure we show loader while (re)initializing
        setLoading(true);

        // 1) Get user's related groupIds
        const activitiesRef = collection(db, 'search_activities');
        const q = query(
          activitiesRef,
          where('ownerId', '==', user.uid),
          where('status', 'in', ['confirmed', 'finished'])
        );
        const snapshot = await getDocs(q);
        const groupIds = snapshot.docs.map(d => d.data().groupId).filter(Boolean);

        // 2) If no groups, stop loading and show empty
        if (groupIds.length === 0) {
          setChats([]);
          setLoading(false);
          return;
        }


        //Fetch group metadata once
        const groupMap = {};
        const groupFetches = groupIds.map(async (groupId) => {
          const groupRef = doc(db, 'groups', groupId);
          const groupSnap = await getDoc(groupRef);
          if (groupSnap.exists()) {
            groupMap[groupId] = groupSnap.data();
          }
        });
        await Promise.all(groupFetches);


        // 3) Subscribe to each chat and wait until the FIRST emission of ALL
        const pending = new Set(groupIds);

        unsubscribers = groupIds.map(groupId => {
          const chatRef = doc(db, 'chats', groupId);
          let firstEmission = true;

          return onSnapshot(chatRef, async (chatSnap) => {
            if (chatSnap.exists()) {
              const chatData = { id: chatSnap.id, ...chatSnap.data() };

              // Add group metadata
              const groupData = groupMap[groupId];
              chatData.activityType = groupData?.activityType || groupId;

              // Fetch lastRead timestamp for current user
              const readRef = doc(db, `chats/${chatSnap.id}/chat_reads/${user.uid}`);
              const readSnap = await getDoc(readRef);
              const lastRead = readSnap.exists() ? readSnap.data().lastRead : null;
              chatData.lastRead = lastRead;

              setChats(prev => {
                const filtered = prev.filter(c => c.id !== chatSnap.id);
                return [chatData, ...filtered].sort(
                  (a, b) => (b.lastUpdated?.toDate() || 0) - (a.lastUpdated?.toDate() || 0)
                );
              });
            } else {
              // If chat doc doesn't exist, consider it "emitted" to avoid locking the loader
              setChats(prev => prev.filter(c => c.id !== groupId));
            }

            // Mark first emission for this chat
            if (firstEmission) {
              firstEmission = false;
              pending.delete(groupId);
              if (pending.size === 0) {
                // All initial snapshots received — hide loader
                setLoading(false);
              }
            }
          });
        });
      } catch (error) {
        console.error('Error fetching chats:', error);
        setLoading(false);
      }
    });

    // Cleanup
    return () => {
      unsubscribers.forEach(unsub => unsub && unsub());
      unsubscribeAuth();
    };
  }, [navigate]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString();
  };

  const getInitial = (str) => (str?.charAt(0)?.toUpperCase() || '?');

  const isUnread = (chat) => {
    if (!chat.lastUpdated || !chat.lastRead) return true;
    return chat.lastUpdated.toDate() > chat.lastRead.toDate();
  };

  // Generate a pleasant gradient from a string (stable per chat)
  const gradientFor = (seed) => {
    const [h1, h2] = hslPair(seed);
    return `linear-gradient(135deg, hsl(${h1} 85% 60%) 0%, hsl(${h2} 85% 55%) 100%)`;
  };
  const hslPair = (seed) => {
    const h = hash(seed) % 360;
    const h2 = (h + 30) % 360;
    return [h, h2];
  };
  const hash = (str = '') => {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };

  const openChat = (chat) => navigate(`/chat/${chat.groupId || chat.id}`);

  return (
    <>
      {/* Fixed full-page gradient backdrop */}
      <div className="cp-bg" aria-hidden="true" />

      <div className="cp-page">
        {/* Header */}
        <header className="cp-hero">
          <div className="cp-hero-inner">
            <h1 className="cp-title">Your Chats</h1>
            {/* <p className="cp-subtitle">Continue conversations and see what’s new at a glance.</p> */}
          </div>
        </header>

        {/* Loading overlay (prevents empty-state flicker) */}
        {loading && (
          <div className="cp-loader-overlay" aria-live="polite" aria-busy="true">
            <div className="cp-spinner" />
            <div className="cp-loading-text">Loading chats…</div>
          </div>
        )}

        {/* Content */}
        <main className="cp-content" aria-hidden={loading}>
          {!loading && (chats.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="cp-list" aria-label="Your chats">
              {chats.map((chat) => {
                const unread = isUnread(chat);
                const title = chat.activityType || 'Chat';
                const time = chat.lastUpdated ? formatTimestamp(chat.lastUpdated) : '';
                return (
                  <li
                    key={chat.id}
                    className={`cp-item ${unread ? 'unread' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openChat(chat)}
                    onKeyDown={(e) => e.key === 'Enter' && openChat(chat)}
                    aria-label={`Open chat ${title}`}
                  >
                    <div
                      className="cp-avatar"
                      style={{ background: gradientFor(chat.chatId || chat.id) }}
                    >
                      <span className="cp-initial">{getInitial(title)}</span>
                    </div>

                    <div className="cp-main">
                      <div className="cp-row">
                        <div className="cp-name" title={title}>{title}</div>
                        <time className="cp-time" dateTime={chat.lastUpdated?.toDate()?.toISOString?.() || ''}>
                          {time}
                        </time>
                      </div>
                      <div className="cp-row">
                        <div className="cp-preview" title={chat.lastMessage || 'No messages yet'}>
                          {chat.lastMessage || 'No messages yet'}
                        </div>
                        {unread && <span className="cp-unread-dot" aria-label="unread messages" />}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ))}
        </main>
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <section className="cp-empty">
      <div className="cp-empty-illustration" aria-hidden="true">
        <svg width="140" height="120" viewBox="0 0 140 120" fill="none">
          <defs>
            <linearGradient id="cg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--cp-grad-a)" />
              <stop offset="100%" stopColor="var(--cp-grad-b)" />
            </linearGradient>
          </defs>
          <rect x="12" y="22" rx="12" ry="12" width="116" height="76" fill="url(#cg)" opacity="0.25" />
          <rect x="24" y="36" rx="6" ry="6" width="70" height="10" fill="url(#cg)" opacity="0.35" />
          <rect x="24" y="56" rx="6" ry="6" width="92" height="10" fill="url(#cg)" opacity="0.35" />
          <circle cx="36" cy="84" r="10" fill="url(#cg)" opacity="0.35" />
        </svg>
      </div>
      <h2 className="cp-empty-title">No chats yet</h2>
      <p className="cp-empty-subtitle">Start a conversation and it will show up here.</p>
    </section>
  );
}

export default ChatsPage;